import React, { useState } from "react";
import { isEmail } from "validator";
import styles from "./SignupForm.module.css";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { addUserIfNotExists, checkCouponCode } from "../../dbApiFunctions";
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth';
import { hashString } from "../../utils/utilities";
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert } from "../../utils/sweetAlertConfig";
import { copyBaseUserDataTemplate } from "../../config/aws";
import "../../styles/sweetAlert.css";
import "../loginRegister/loginStyle.css";

const REGISTER_IMAGE = "https://cdn.builder.io/api/v1/image/assets/TEMP/129d10dca315d7fa973b167433d4c262f01e67e2?width=2086";

export default function Register({ setIsLoggedIn, setFormType }) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    couponCode: "",
    showPassword: false,
    showConfirmPassword: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3 || formData.username.length > 20) {
      newErrors.username = "Username must be between 3 and 20 characters";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    //  else if (formData.email.toLowerCase().endsWith("@gmail.com") || 
    //            formData.email.toLowerCase().endsWith("@yahoo.com") || 
    //            formData.email.toLowerCase().endsWith("@hotmail.com")) {
    //   newErrors.email = "Only business emails are allowed";
    // }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.replace(/\D/g, '').length < 8 || formData.phone.replace(/\D/g, '').length > 15) {
      newErrors.phone = "Please enter a valid phone number (8-15 digits)";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6 || formData.password.length > 40) {
      newErrors.password = "Password must be between 6 and 40 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({ ...prev, phone }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: "" }));
    }
  };

  const toggleShowPassword = () => {
    setFormData((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const toggleShowConfirmPassword = () => {
    setFormData((prev) => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }));
  };

  const handleRegistration = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsRegistering(true);

    let creditsToUse = 100;

    // Validate coupon code if provided
    if (formData.couponCode) {
      try {
        const couponResponse = await checkCouponCode(formData.couponCode);
        if (!couponResponse.status) {
          showErrorAlert(
            'Invalid Coupon',
            couponResponse.message,
            'Please check your coupon code and try again'
          );
          setIsRegistering(false);
          return;
        }
        creditsToUse = creditsToUse + couponResponse.credits;
      } catch (error) {
        console.error(error);
        showErrorAlert(
          'Coupon Validation Error',
          'Error validating coupon code',
          'Please try again or contact support'
        );
        setIsRegistering(false);
        return;
      }
    }

    try {
      // Create Firebase authentication account
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Send email verification
      setIsSendingEmailVerification(true);
      try {
        await sendEmailVerification(userCredential.user);
        setEmailVerificationSent(true);

        showSuccessAlert(
          'Email Verification Sent!',
          'Please check your email and click the verification link.',
          'You must verify your email before completing registration.'
        );

        // Store user data temporarily (don't complete registration yet)
        const hashedEmail = await hashString(formData.email);
        sessionStorage.setItem("temp_username", hashedEmail);
        sessionStorage.setItem("temp_email", formData.email);
        sessionStorage.setItem("temp_phone", formData.phone);
        sessionStorage.setItem("temp_password", formData.password);
        sessionStorage.setItem("temp_credits", creditsToUse.toString());
        sessionStorage.setItem("temp_username_value", formData.username);

        const response = await addUserIfNotExists(
          formData.username,
          formData.phone,
          formData.email,
          formData.password,
          creditsToUse
        );

        if (response.status) {
          await copyBaseUserDataTemplate(hashedEmail);
        }

        // Show verification reminder
        showInfoAlert(
          'Email Verification Required',
          'Please check your inbox and click the verification link.',
          'You can resend the verification email if needed.'
        );

      } catch (verificationError) {
        console.error("Email verification error:", verificationError);

        // Clean up Firebase auth user if verification fails
        try {
          await userCredential.user.delete();
        } catch (deleteError) {
          console.error("Error cleaning up Firebase auth user:", deleteError);
        }

        showErrorAlert(
          'Email Verification Failed',
          'Failed to send verification email.',
          'Please try again or contact support.'
        );
      }

    } catch (firebaseError) {
      console.error("Firebase registration error:", firebaseError);

      // Handle specific Firebase error codes
      let errorMessage = "Registration failed. Please try again.";
      let errorTitle = "Registration Error";
      let errorDetails = "An unexpected error occurred during registration.";

      switch (firebaseError.code) {
        case 'auth/email-already-in-use':
          try {
            const auth = getAuth();
            // Try to sign in with the provided credentials
            const existingUserCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
            if (!existingUserCredential.user.emailVerified) {
              // Resend verification email
              await sendEmailVerification(existingUserCredential.user);
              errorMessage = "Email Not Verified";
              errorTitle = "Verification Email Resent";
              errorDetails = "A verification email has been resent to your address. Please check your inbox and click the verification link to activate your account.";
              setEmailVerificationSent(true);
            }
          } catch (signInError) {
            // If sign-in fails, likely due to wrong password
            errorMessage = "An account with this email already exists.";
            errorTitle = "Email Already Exists (Reset Password from Login Page)";
            errorDetails = "Please use a different email or try logging in instead.";
          }
          break;
        case 'auth/invalid-email':
          errorMessage = "Please enter a valid email address.";
          errorTitle = "Invalid Email";
          errorDetails = "The email format is not recognized.";
          break;
        case 'auth/operation-not-allowed':
          errorMessage = "Email/password accounts are not enabled.";
          errorTitle = "Registration Disabled";
          errorDetails = "Please contact support for assistance.";
          break;
        case 'auth/weak-password':
          errorMessage = "Password is too weak.";
          errorTitle = "Weak Password";
          errorDetails = "Please choose a stronger password (at least 6 characters).";
          break;
        case 'auth/network-request-failed':
          errorMessage = "Network error. Please check your connection.";
          errorTitle = "Network Error";
          errorDetails = "Please ensure you have a stable internet connection.";
          break;
        default:
          errorMessage = "Registration failed. Please try again.";
          errorTitle = "Registration Error";
          errorDetails = "An unexpected error occurred. Please contact support if the problem persists.";
      }

      showErrorAlert(
        errorTitle,
        errorMessage,
        errorDetails
      );
    } finally {
      setIsRegistering(false);
      setIsSendingEmailVerification(false);
    }
  };

  const handleResendEmailVerification = async () => {
    setIsSendingEmailVerification(true);

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        await sendEmailVerification(currentUser);
        showSuccessAlert(
          'Verification Email Resent!',
          'Please check your inbox for the verification link.',
          'Make sure to check your spam folder as well.'
        );
      } else {
        showErrorAlert(
          'User Not Found',
          'Please try registering again.',
          'The user session has expired.'
        );
      }
    } catch (error) {
      console.error("Error resending verification email:", error);
      showErrorAlert(
        'Failed to Resend',
        'Error sending verification email.',
        'Please try again or contact support.'
      );
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  const handleEmailVerificationCheck = async () => {
    try {
      const auth = getAuth();
      await auth.currentUser?.reload();

      if (auth.currentUser?.emailVerified) {

        // Show success alert for login and account setup
        showSuccessAlert(
          'Login Successful!',
          'Setting up your account, please wait...',
          'Your email has been verified and we are preparing your account.'
        );

        // Email is verified, complete registration
        const tempEmail = sessionStorage.getItem("temp_email");
        const tempPassword = sessionStorage.getItem("temp_password");

        const hashedEmail = await hashString(tempEmail);
        await copyBaseUserDataTemplate(hashedEmail);

        // Set session storage
        sessionStorage.setItem("username", hashedEmail);
        sessionStorage.setItem("email", tempEmail);
        sessionStorage.setItem("password", tempPassword);

        // Clean up temporary data
        sessionStorage.removeItem("temp_username");
        sessionStorage.removeItem("temp_email");
        sessionStorage.removeItem("temp_phone");
        sessionStorage.removeItem("temp_password");
        sessionStorage.removeItem("temp_credits");
        sessionStorage.removeItem("temp_username_value");

        setIsLoggedIn(true);

        showSuccessAlert(
          'Registration Complete!',
          'Your email has been verified and account created successfully.',
          'Setting up your account... You will be redirected shortly.'
        );

        // Reload after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } else {
        showWarningAlert(
          'Email Not Verified',
          'Please check your email and click the verification link.',
          'If you haven\'t received the email, try resending it.'
        );
      }
    } catch (error) {
      console.error("Error checking email verification:", error);
      showErrorAlert(
        'Verification Check Failed',
        'Error checking email verification status.',
        'Please try again or contact support.'
      );
    }
  };

  const handleSignInClick = (e) => {
    e.preventDefault();
    setFormType("login");
  };

  // Show email verification section if verification was sent
  if (emailVerificationSent) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            marginBottom: '32px'
          }}>
            <span style={{
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(270deg, #7040ff 0%, #4b1bf8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              Shutter
            </span>
          </div>

          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '32px',
            fontFamily: "'Plus Jakarta Sans', sans-serif"
          }}>
            Email Verification Required
          </h2>

          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
            border: '1px solid #0ea5e9',
            borderRadius: '16px',
            padding: '32px 24px',
            marginBottom: '32px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(2, 132, 199, 0.1))',
              borderRadius: '50%'
            }} />

            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
              position: 'relative',
              zIndex: 1
            }}>
              <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'white' }}>
                <path d="M22 2H2l8 9.46V22l4-2v-8.54L22 2z" />
              </svg>
            </div>

            <h3 style={{
              color: '#0c4a6e',
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '16px',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              Check Your Email
            </h3>

            <p style={{
              color: '#0369a1',
              fontSize: '15px',
              lineHeight: '1.6',
              marginBottom: '12px',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              We've sent a verification email to:
            </p>

            <div style={{
              background: 'rgba(14, 165, 233, 0.1)',
              border: '1px solid rgba(14, 165, 233, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'inline-block'
            }}>
              <p style={{
                color: '#0c4a6e',
                fontSize: '16px',
                fontWeight: '600',
                margin: '0',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {formData.email}
              </p>
            </div>

            <p style={{
              color: '#0369a1',
              fontSize: '15px',
              lineHeight: '1.6',
              marginBottom: '0',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              Please check your inbox and click the verification link to complete your registration.
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <button
              onClick={handleEmailVerificationCheck}
              style={{
                cursor: 'pointer',
                background: 'linear-gradient(270deg, #7040ff 0%, #4b1bf8 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 24px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(112, 64, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(112, 64, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(112, 64, 255, 0.3)';
              }}
            >
              I've Verified My Email
            </button>

            <button
              onClick={handleResendEmailVerification}
              disabled={isSendingEmailVerification}
              style={{
                cursor: isSendingEmailVerification ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(270deg, #6b7280 0%, #4b5563 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 24px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                transition: 'all 0.2s ease',
                opacity: isSendingEmailVerification ? 0.7 : 1,
                boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isSendingEmailVerification) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
              }}
            >
              {isSendingEmailVerification ? "Sending..." : "Resend Email"}
            </button>
          </div>

          <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <p style={{
              color: '#6b7280',
              fontSize: '14px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              margin: '0'
            }}>
              Didn't receive the email? Check your spam folder
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-split-layout-half">
      <div className="auth-left-half">
        <div className="auth-branding">
          <span className="auth-logo">Shutter</span>
          <a href="/" className="auth-back-link">Back to Home</a>
        </div>
        <div className="auth-form-card">
          <form className={styles.formContainer} onSubmit={handleRegistration}>
            <h2 className="auth-title">Create your account</h2>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Username</label>
              <div className={styles.inputField}>
                <input
                  type="text"
                  name="username"
                  className={styles.input}
                  placeholder="Choose a username"
                  value={formData.username}
                  onChange={handleInputChange}
                  autoComplete="username"
                  disabled={isRegistering}
                />
              </div>
              {errors.username && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '3px'
                }}>
                  {errors.username}
                </div>
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Email Address</label>
              <div className={styles.inputField}>
                <input
                  type="email"
                  name="email"
                  className={styles.input}
                  placeholder="example@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  disabled={isRegistering}
                />
              </div>
              {errors.email && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '3px'
                }}>
                  {errors.email}
                </div>
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Mobile Number</label>
              <div className={styles.inputField} style={{ padding: 0, height: '44px', display: 'flex', alignItems: 'center' }}>
                <PhoneInput
                  country={'us'}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    className: styles.input,
                    autoFocus: false,
                    placeholder: '+1',
                    style: {
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      background: 'transparent',
                      height: '100%',
                      padding: '0 16px',
                      paddingLeft: '60px'
                    },
                    disabled: isRegistering
                  }}
                  inputStyle={{
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    background: 'transparent',
                    fontFamily: 'inherit',
                    fontSize: '16px',
                    height: '100%',
                    padding: '0 16px',
                    paddingLeft: '60px'
                  }}
                  buttonStyle={{
                    border: 'none',
                    borderRight: '1px solid #e5e7eb',
                    background: 'transparent',
                    padding: '0',
                    margin: '0',
                    width: '50px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  containerStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0'
                  }}
                  dropdownStyle={{ borderRadius: '0 0 6px 6px' }}
                  disabled={isRegistering}
                />
              </div>
              {errors.phone && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '3px'
                }}>
                  {errors.phone}
                </div>
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Password</label>
              <div className={styles.inputField}>
                <input
                  type={formData.showPassword ? "text" : "password"}
                  name="password"
                  className={styles.input}
                  placeholder="********"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  disabled={isRegistering}
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#bdbdbd',
                    fontSize: 18,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                  aria-label={formData.showPassword ? "Hide password" : "Show password"}
                  disabled={isRegistering}
                >
                  {formData.showPassword ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5.05 0-9.29-3.36-11-8 1.02-2.53 2.84-4.64 5.06-6.06M1 1l22 22" /><path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5c1.93 0 3.5-1.57 3.5-3.5 0-.47-.09-.92-.26-1.33M14.47 14.47A3.5 3.5 0 0 1 12 8.5c-.47 0-.92.09-1.33.26" /></svg>
                  ) : (
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><ellipse cx="12" cy="12" rx="10" ry="6" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '3px'
                }}>
                  {errors.password}
                </div>
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Confirm Password</label>
              <div className={styles.inputField}>
                <input
                  type={formData.showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  className={styles.input}
                  placeholder="********"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  disabled={isRegistering}
                />
                <button
                  type="button"
                  onClick={toggleShowConfirmPassword}
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#bdbdbd',
                    fontSize: 18,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                  aria-label={formData.showConfirmPassword ? "Hide password" : "Show password"}
                  disabled={isRegistering}
                >
                  {formData.showConfirmPassword ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5.05 0-9.29-3.36-11-8 1.02-2.53 2.84-4.64 5.06-6.06M1 1l22 22" /><path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5c1.93 0 3.5-1.57 3.5-3.5 0-.47-.09-.92-.26-1.33M14.47 14.47A3.5 3.5 0 0 1 12 8.5c-.47 0-.92.09-1.33.26" /></svg>
                  ) : (
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><ellipse cx="12" cy="12" rx="10" ry="6" /><circle cx="12" cy="12" r="3" /></svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  marginTop: '3px'
                }}>
                  {errors.confirmPassword}
                </div>
              )}
            </div>
            {/* <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Coupon Code (Optional)</label>
              <div className={styles.inputField}>
                <input
                  type="text"
                  name="couponCode"
                  className={styles.input}
                  placeholder="Enter coupon code"
                  value={formData.couponCode}
                  onChange={handleInputChange}
                  disabled={isRegistering}
                />
              </div>
            </div> */}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isRegistering}
              style={{ opacity: isRegistering ? 0.7 : 1 }}
            >
              <span className={styles.buttonText}>
                {isRegistering ? "Creating Account..." : "Create an Account"}
              </span>
            </button>
            <div className={styles.signInSection}>
              <p className={styles.signInText}>
                Already have an account?
                <a href="#" className={styles.signInLink} onClick={handleSignInClick}>
                  Sign in
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
      <div className="auth-right-half" style={{ backgroundImage: `url('${REGISTER_IMAGE}')` }} />
    </div>
  );
}